import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';

/**
 * Props for `ImageSelect`.
 */
export type ImageSelectProps = SliceComponentProps<Content.ImageSelectSlice> & {
  context: {
    register: UseFormRegister<FieldValues>;
    errors: FieldErrors<FieldValues>;
    watch: UseFormWatch<FieldValues>;
  };
};

/**
 * Component for "ImageSelect" Slices.
 */
const ImageSelect = ({ slice, context }: ImageSelectProps): JSX.Element => {
  const { technologies } = slice.primary;
  const { register, watch, errors } = context;
  const selectedTechnologies: string[] = watch('selectedTechnologies') || [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-12"
    >
      {technologies.map((technology, index) => {
        const isSelected = selectedTechnologies.includes(technology.label!);
        return (
          <label
            key={`icon-${index}`}
            className={`p-8 border backdrop-blur-md rounded-[20px] text-center hover:shadow-lg 
              ${isSelected ? 'border-blue-500 bg-white/50' : 'border-[#658a72] bg-white/35 '}`}
          >
            <input
              type="checkbox"
              className="hidden"
              value={technology.label!}
              {...register('selectedTechnologies', {
                validate: (values) =>
                  values?.length > 0 || 'Select at least one skill',
              })}
            />
            <div className="flex flex-col gap-y-3 items-center">
              <PrismicNextImage
                field={technology.icon}
                className="w-14 h-14 object-contain"
              />
              <span className="font-normal text-[12px] lg:text-lg">
                {technology.label}
              </span>
            </div>
          </label>
        );
      })}
      {errors['selectedTechnologies'] && (
        <span className="text-error text-sm col-span-2">
          {errors['selectedTechnologies']?.message as string}
        </span>
      )}
    </section>
  );
};

export default ImageSelect;
